// src/features/scheduleGenerator/components/SkeletonModal.tsx
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowLeft, SendHorizontal } from 'lucide-react';
import { format, isToday, isTomorrow, parseISO } from 'date-fns';
import { SkeletonTimeBlock, SkeletonEvent, SkeletonAlldayEvent } from '../services/scheduleGeneratorService';
import { timeBoxCategoryConfig } from '../../../constants/timebox.constants';
import { eventCategoryConfig, eventUnifiedColors } from '../../../constants/event.constants';
import { alldayEventColors } from '../../../types/calendar-event';
import { useMediaQuery } from '../../../hooks/useMediaQuery';

const BASE_HEIGHT = 44;
const BASE_MINS = 30;
const PX_PER_MIN = 0.8;

function parseMins(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

function calcDuration(startTime: string, endTime: string): number {
  return parseMins(endTime) - parseMins(startTime);
}

function formatDraftDate(dateStr: string): string {
  const d = parseISO(dateStr);
  if (isToday(d)) return 'Today';
  if (isTomorrow(d)) return 'Tomorrow';
  return format(d, 'MMM d');
}

// 時間軸的渲染項目：timebox 或 timed event
type TimelineItem =
  | { kind: 'block'; data: SkeletonTimeBlock }
  | { kind: 'event'; data: SkeletonEvent };

function buildTimelineItems(
  blocks: SkeletonTimeBlock[],
  events: SkeletonEvent[]
): TimelineItem[] {
  const items: TimelineItem[] = [
    ...blocks.map(b => ({ kind: 'block' as const, data: b })),
    ...events.map(e => ({ kind: 'event' as const, data: e })),
  ];
  return items.sort((a, b) => parseMins(a.data.startTime) - parseMins(b.data.startTime));
}

interface SkeletonModalProps {
  isOpen: boolean;
  onClose: () => void;
  date: string;
  timeBlocks: SkeletonTimeBlock[];
  timedEvents: SkeletonEvent[];
  alldayEvents: SkeletonAlldayEvent[];
  isGenerating: boolean;
  isApplying: boolean;
  onFinish: () => void;
  onFillTasks: () => void;
  onBack: () => void;
  onFineTune: (instruction: string) => void;
}

// All-day events 列表（時間軸上方固定區域）
const AllDayEventList: React.FC<{ events: SkeletonAlldayEvent[] }> = ({ events }) => {
  if (events.length === 0) return null;
  return (
    <div className="px-4 pt-3 pb-2 flex flex-col gap-1.5 border-b border-neutral-100">
      {events.map((event, i) => {
        const config = eventCategoryConfig[event.category] ?? eventCategoryConfig['other'];
        const Icon = config.icon;
        return (
          <div
            key={i}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg ${alldayEventColors}`}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="text-sm font-medium leading-snug">{event.title}</span>
          </div>
        );
      })}
    </div>
  );
};

// 時間軸列表：timeBlocks + timed events 合併排序渲染
const SkeletonTimeline: React.FC<{
  blocks: SkeletonTimeBlock[];
  timedEvents: SkeletonEvent[];
}> = ({ blocks, timedEvents }) => {
  const items = buildTimelineItems(blocks, timedEvents);

  return (
    <div className="px-4 py-3">
      {items.map((item, i) => {
        const prevItem = i > 0 ? items[i - 1] : null;
        const gapHeight = prevItem
          ? Math.max(0, parseMins(item.data.startTime) - parseMins(prevItem.data.endTime)) * PX_PER_MIN
          : 0;

        if (item.kind === 'event') {
          const event = item.data;
          const config = eventCategoryConfig[event.category] ?? eventCategoryConfig['other'];
          const Icon = config.icon;
          const duration = calcDuration(event.startTime, event.endTime);
          const extensionMins = Math.max(0, duration - BASE_MINS);
          const blockHeight = BASE_HEIGHT + extensionMins * PX_PER_MIN;

          return (
            <React.Fragment key={`event-${event.startTime}-${i}`}>
              {gapHeight > 0 && (
                <div className="flex" style={{ height: gapHeight }}>
                  <div className="w-14 shrink-0" />
                  <div className="flex-1 border-l border-dashed border-neutral-200 ml-2" />
                </div>
              )}
              <div className="flex gap-2" style={{ height: blockHeight - 2, marginBottom: 2 }}>
                <div className="w-14 shrink-0 flex flex-col items-end pt-1.5">
                  <span className="text-xs font-mono text-neutral-400 leading-none">
                    {event.startTime}
                  </span>
                  {i === items.length - 1 && (
                    <span className="text-xs font-mono text-neutral-400 leading-none mt-auto">
                      {event.endTime}
                    </span>
                  )}
                </div>
                <div className={`flex-1 flex flex-col justify-between px-3 py-2 rounded-lg ${eventUnifiedColors}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium leading-snug truncate">{event.title}</p>
                    <span className={`flex items-center justify-center gap-1 text-xs px-1.5 py-0.5 rounded-md shrink-0 w-[72px] ${eventUnifiedColors}`}>
                      <Icon className="h-3 w-3 shrink-0" />
                      {config.label}
                    </span>
                  </div>
                  {extensionMins > 0 && (
                    <p className="text-xs font-mono opacity-60">
                      {event.startTime} – {event.endTime}
                    </p>
                  )}
                </div>
              </div>
            </React.Fragment>
          );
        }

        // timebox
        const block = item.data as SkeletonTimeBlock;
        const config = timeBoxCategoryConfig[block.category as keyof typeof timeBoxCategoryConfig]
          || timeBoxCategoryConfig['focus'];
        const duration = calcDuration(block.startTime, block.endTime);
        const extensionMins = Math.max(0, duration - BASE_MINS);
        const blockHeight = BASE_HEIGHT + extensionMins * PX_PER_MIN;
        const Icon = config.icon;

        return (
          <React.Fragment key={`block-${block.startTime}-${i}`}>
            {gapHeight > 0 && (
              <div className="flex" style={{ height: gapHeight }}>
                <div className="w-14 shrink-0" />
                <div className="flex-1 border-l border-dashed border-neutral-200 ml-2" />
              </div>
            )}
            <div className="flex gap-2" style={{ height: blockHeight - 2, marginBottom: 2 }}>
              <div className="w-14 shrink-0 flex flex-col items-end pt-1.5">
                <span className="text-xs font-mono text-neutral-400 leading-none">
                  {block.startTime}
                </span>
                {i === items.length - 1 && (
                  <span className="text-xs font-mono text-neutral-400 leading-none mt-auto">
                    {block.endTime}
                  </span>
                )}
              </div>
              <div className={`flex-1 flex flex-col justify-between px-3 py-2 rounded-lg ${config.actionBg}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-neutral-800 leading-snug truncate">
                    {block.title}
                  </p>
                  <span className={`flex items-center justify-center gap-1 text-xs px-1.5 py-0.5 rounded-md shrink-0 w-[72px] ${config.color}`}>
                    <Icon className="h-3 w-3 shrink-0" />
                    {config.label}
                  </span>
                </div>
                {extensionMins > 0 && (
                  <p className="text-xs text-neutral-400 font-mono">
                    {block.startTime} – {block.endTime}
                  </p>
                )}
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

const SkeletonModal: React.FC<SkeletonModalProps> = ({
  isOpen,
  onClose,
  date,
  timeBlocks,
  timedEvents,
  alldayEvents,
  isGenerating,
  isApplying,
  onFinish,
  onFillTasks,
  onBack,
  onFineTune,
}) => {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [fineTuneText, setFineTuneText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) setFineTuneText('');
  }, [isOpen]);

  const handleFineTune = () => {
    const text = fineTuneText.trim();
    if (!text) return;
    onFineTune(text);
    setFineTuneText('');
  };

  const hasContent = timeBlocks.length > 0 || timedEvents.length > 0;

  // 手機：底部滑入；桌機：輕微下移 + fade
  const cardVariants = isDesktop
    ? {
        initial: { opacity: 0, y: -8 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -8 },
      }
    : {
        initial: { y: '100%' },
        animate: { y: 0 },
        exit: { y: '100%' },
      };

  const cardTransition = isDesktop
    ? ({ duration: 0.2, ease: 'easeOut' } as const)
    : ({ type: 'spring', damping: 30, stiffness: 300 } as const);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-modal-nested flex justify-center items-end sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-label="Your Day Outline"
        >
          {/* backdrop */}
          <motion.div
            className="absolute inset-0 bg-neutral-900 bg-opacity-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />

          <motion.div
            className="relative bg-white rounded-t-xl md:rounded-lg w-full sm:max-w-md sm:mx-auto shadow-xl flex flex-col max-h-[85vh]"
            variants={cardVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={cardTransition}
          >
            {/* drag handle */}
            <div className="sm:hidden w-full flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-neutral-300 rounded-full" />
            </div>

            {/* header */}
            <div className="flex justify-between items-center px-4 pt-3 pb-3 shrink-0">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-neutral-800">Your Day Outline</h2>
                <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-neutral-100 text-neutral-500">
                  {formatDraftDate(date)}
                </span>
              </div>
              <button
                onClick={onClose}
                disabled={isApplying}
                className="text-neutral-400 hover:text-neutral-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* all-day events + time blocks list */}
            <div className="overflow-y-auto scrollbar-hide flex-1 border-t border-neutral-100">
              {isGenerating ? (
                <div className="flex flex-col items-center justify-center py-12 gap-2">
                  <svg className="animate-spin h-5 w-5 text-sky-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span className="text-sm text-neutral-400">Generating skeleton…</span>
                </div>
              ) : !hasContent ? (
                <div className="flex flex-col items-center justify-center py-12 gap-1">
                  <p className="text-sm text-neutral-500">No available slots today.</p>
                  <p className="text-xs text-neutral-400">You can still finish here or adjust manually.</p>
                </div>
              ) : (
                <>
                  <AllDayEventList events={alldayEvents} />
                  <SkeletonTimeline blocks={timeBlocks} timedEvents={timedEvents} />
                </>
              )}
            </div>

            {/* fine-tune input */}
            <div className="px-4 py-3 border-t border-neutral-200 shrink-0">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Tweak this outline..."
                  value={fineTuneText}
                  onChange={e => setFineTuneText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleFineTune(); }}
                  disabled={isGenerating}
                  className="flex-1 text-sm border border-neutral-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-sky-400 focus:border-sky-400 placeholder:text-neutral-400 disabled:opacity-50"
                />
                <button
                  type="button"
                  onClick={handleFineTune}
                  disabled={!fineTuneText.trim() || isGenerating}
                  className="p-2 text-sky-500 hover:text-sky-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label="Send adjustment"
                >
                  <SendHorizontal className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* footer */}
            <div className="flex items-center justify-between gap-3 px-4 pt-3 pb-safe shrink-0">
              {isApplying ? (
                <div className="flex w-full items-center justify-center gap-2 py-1">
                  <svg className="animate-spin h-4 w-4 text-sky-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span className="text-sm text-neutral-500">Applying schedule…</span>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={onBack}
                    className="flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700 transition-colors px-1"
                    disabled={isGenerating}
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </button>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={onFinish}
                      disabled={isGenerating || !hasContent}
                      className="btn-secondary btn-sm !py-[7px]"
                    >
                      Finish
                    </button>
                    <button
                      type="button"
                      onClick={onFillTasks}
                      disabled={isGenerating || !hasContent}
                      className="btn-primary btn-sm"
                    >
                      Assign tasks →
                    </button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default SkeletonModal;
